import { Component, OnInit } from '@angular/core';
import { CashPositions } from '../../_models/cash-positions';
import { HttpRequestService } from '../../_services/http-request.service';
import { Chart } from 'angular-highcharts';
import { GenerateGaugeServiceService } from 'src/app/_services/generate-gauge-service.service';
import { CurrentPortfolio } from 'src/app/_models/current-portfolio';
import { Equity, Assets } from 'src/app/_models/equity';
import { GenerateColumnChart } from 'src/app/_services/generate-column-chart';
import { CurrencyPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-portfolio-composition',
  templateUrl: './portfolio-composition.component.html',
  styleUrls: ['./portfolio-composition.component.css']
})

export class PortfolioCompositionComponent implements OnInit {
   
  MAX_DAYS_FOR_DAILY_CHART = 69;
   
  startCashPositions: CashPositions[] = [];
  endCashPositions: CashPositions[] = [];

  accountBaseCurrency: string;

  allAvailableAssets = ['Bonds', 'Cash', 'Commodities', 'Funds', 'Notes', 'Options', 'Stock'];

  chartCash: Chart;
  chartStock: Chart;
  chartBond: Chart;
  chartOption: Chart;
  chartCommodity: Chart;
  chartFund: Chart;

  columnChart: Chart;

  percentagePerAsset = {
    cash: 0,
    stock: 0,
    bonds: 0,
    options: 0,
    funds: 0,
    commodities: 0
  };

  countedAssets = {
    stock: 0,
    bonds: 0,
    options: 0,
    funds: 0,
    commodities: 0
  };

  totalValuesAssets = {
    total: 0,
    stock: 0,
    options: 0,
    funds: 0,
    bonds: 0,
    commodities: 0,
    cash: 0
  };

  constructor(
    public httpRequestService: HttpRequestService,
    private gaugeChartGenerator: GenerateGaugeServiceService,
    private columnChartGenerator: GenerateColumnChart,
    private translate: TranslateService) { }

  ngOnInit() {
    this.getCashPositions();
    this.getPortfolioDataAndProcess();
    this.getEquityAndProcess();
  }

  getEquityAndProcess() {
    return this.httpRequestService.equityData.pipe(tap(responseData => {
      this.resetTotalValueAssets();
      if (responseData.length === 0) {
        return;
      }
      if (this.endDateMatchesEquity(responseData)) {
        this.processEquityData(responseData);
        this.generateCharts(responseData);
      }
    })).toPromise();
  }

  getPortfolioDataAndProcess() {
    return this.httpRequestService.currentPortfolioData.pipe(tap(responseData => {
      this.resetCountedAssets();
      if (responseData.length === 0) {
        return;
      }
      this.processPortfolioData(responseData);
    })).toPromise();
  }

  getCashPositions() {
    return this.httpRequestService.cashPositionsData.pipe(tap(responseData => {
      this.resetCashPositions();
      if (responseData.length === 0) {
        return;
      }
      this.startCashPositions = responseData.startCashPositions;
      this.endCashPositions = responseData.endCashPositions;
    })).toPromise();
  }

  generateCharts(equityData: Equity[]) {
    this.createGaugeCharts();
    this.createBarChart(equityData);
    this.handleChartTranslation();
  }

  processEquityData(equityData: Equity[]) {
    let assetDataForLastDay = equityData[equityData.length - 1].assets;
    this.accountBaseCurrency = equityData[equityData.length - 1].currency;

    this.calculateTotalValuePerAsset(assetDataForLastDay);
    this.calculatePercentagePerAsset(assetDataForLastDay);
  }

  calculateTotalValuePerAsset(assetDataForLastDay: Assets) {
    Object.entries(this.totalValuesAssets).forEach(
      ([key]) => this.totalValuesAssets[key] = assetDataForLastDay[key]);
  }

  createGaugeCharts() {
    this.chartCash = this.percentagePerAsset.cash !== 0 ? this.gaugeChartGenerator.getChart(this.percentagePerAsset.cash) : null;
    this.chartStock = this.percentagePerAsset.stock !== 0 ? this.gaugeChartGenerator.getChart(this.percentagePerAsset.stock) : null;
    this.chartBond = this.percentagePerAsset.bonds !== 0 ? this.gaugeChartGenerator.getChart(this.percentagePerAsset.bonds) : null;
    this.chartOption = this.percentagePerAsset.options !== 0 ? this.gaugeChartGenerator.getChart(this.percentagePerAsset.options) : null;
    this.chartFund = this.percentagePerAsset.funds !== 0 ? this.gaugeChartGenerator.getChart(this.percentagePerAsset.funds) : null;
    this.chartCommodity = this.percentagePerAsset.commodities !== 0 ? this.gaugeChartGenerator.getChart(this.percentagePerAsset.commodities) : null;
  }
   
  calculatePercentagePerAsset(assetDataForLastDay: Assets) {
    Object.entries(this.percentagePerAsset).forEach(([key]) => {
      if (key === 'cash') {
        this.percentagePerAsset[key] = parseFloat(((assetDataForLastDay[key] + assetDataForLastDay.dividentAccruals + assetDataForLastDay.interestAccruals) * 100 / assetDataForLastDay.total).toFixed(2))
      } else {
        this.percentagePerAsset[key] = parseFloat((assetDataForLastDay[key] * 100 / assetDataForLastDay.total).toFixed(2))
      }
    });
  }
   
  processPortfolioData(portfolioData: CurrentPortfolio[]) {
    portfolioData.forEach(position => {
      switch (position.instrument.securityType) {
        case 'STK':
          this.countedAssets.stock++;
          break;
        case 'BOND':
          this.countedAssets.bonds++;
          break;
        case 'OPT':
          this.countedAssets.options++;
          break;
        case 'FUND':
          this.countedAssets.funds++;
          break;
        case 'CMDTY':
          this.countedAssets.commodities++;
          break;
        default:
          break;
      }
    });
  }


  createBarChart(equityData: Equity[]) {
    this.columnChart = this.columnChartGenerator.getChart();
    this.populateChart(equityData);
  }

  populateChart(equityData: Equity[]) {
    this.addSeriesToChart(equityData);
    this.updateChartProperties(equityData);
  }

  addSeriesToChart(equityData: Equity[]) {
    this.allAvailableAssets.forEach(assetType => {
      let assetTypeCollection = [];
      if (equityData.length < this.MAX_DAYS_FOR_DAILY_CHART)
        assetTypeCollection = this.generateDailySeries(equityData, assetType);
      else
        assetTypeCollection = this.generateWeeklySeries(equityData, assetType);

      if (assetTypeCollection.filter(currentDayData => currentDayData.absolutValue !== 0).length !== 0) {
        this.columnChart.addSeries({
          description: assetType,
          name: assetType,
          data: assetTypeCollection,
          type: undefined
        }, true, false);
      }
    });
  }

  generateWeeklySeries(equityData: Equity[], assetType: string) {
    let assetTypeCollectionByWeek = equityData.reduce((accumulator, currentDayData) => {
      let weekOfTheYear = 'Week ' + moment(currentDayData.date).week() + ' of ' + moment(currentDayData.date).year();
      if (typeof accumulator[weekOfTheYear] === 'undefined') {
        accumulator[weekOfTheYear] = { name: weekOfTheYear, y: 0, absolutValue: 0 };
        accumulator[weekOfTheYear].y = this.calculateAssetPercentage(currentDayData, assetType);
      }
      accumulator[weekOfTheYear].y = (accumulator[weekOfTheYear].y + this.calculateAssetPercentage(currentDayData, assetType)) / 2;
      accumulator[weekOfTheYear].absolutValue += currentDayData.assets[assetType.toLowerCase()];
      return accumulator;
    }, {});
    // Convert to array
    return Object.keys(assetTypeCollectionByWeek).map(key => assetTypeCollectionByWeek[key]);
  }

  generateDailySeries(equityData: Equity[], assetType: string) {
    return equityData.map(currentDayData => {
      return {
        name: moment(currentDayData.date).format('DD/MM/YYYY'),
        y: this.calculateAssetPercentage(currentDayData, assetType),
        absolutValue: currentDayData.assets[assetType.toLowerCase()]
      };
    });
  }

  updateChartProperties(equityData: Equity[]) {
    this.columnChart.ref$.subscribe(responseChart => {
      let categoriesFromFirstSeries = responseChart.series[0].data.map(x => x.name);
      responseChart.update({
        xAxis: {
          type: 'category',
          categories: categoriesFromFirstSeries
        },
        yAxis: {
          labels: {
            formatter: function () {
              return this.value + '%';
            }
          }
        },
        tooltip: {
          useHTML: true,
          shared: true,
          pointFormatter: function () {
            let pointWithColor = '<span class="highcharts-color-' + this.colorIndex + '">\u25CF</span>';
            let absolutValue = (new CurrencyPipe('en-US')).transform(this['absolutValue'], equityData[0].currency, 'symbol');
            let percentageValue = '<small> (' + this.y.toFixed(2) + '%)</small>';
            let nameWithValues = '<span><strong> ' + this.series.name + ': </strong>' + absolutValue + percentageValue + '</span><br>';
            return pointWithColor + nameWithValues;
          }
        }
      }, true, false);
    });
  }

  calculateAssetPercentage(currentDayData: Equity, assetType: string): number {
    if (assetType === "Cash")
      return ((currentDayData.assets[assetType.toLowerCase()] + currentDayData.assets.dividentAccruals + currentDayData.assets.interestAccruals) * 100 / currentDayData.assets.total)
    else
      return (currentDayData.assets[assetType.toLowerCase()] * 100 / currentDayData.assets.total);
  }

  handleChartTranslation() {
    this.translate.stream('portfolio-composition.column-chart').subscribe(translationOf => {
      if (!this.columnChart) return
      this.columnChart.ref$.subscribe(chart => {
        chart.series.forEach(serie => {
          serie.update({
            name: translationOf[serie.options.description.toLowerCase()], type: undefined
          }, true);
        });
      });
    });
  }

  endDateMatchesEquity(equityData: Equity[]) {
    return equityData[equityData.length - 1].date === this.httpRequestService.getTimeframe().endDate.format('YYYY-MM-DD').toString();
  }
   
  resetCashPositions() {
    this.startCashPositions = [];
    this.endCashPositions = [];
  }

  resetCountedAssets() {
    this.countedAssets = {
      stock: 0,
      bonds: 0,
      options: 0,
      funds: 0,
      commodities: 0
    };
  }

  resetTotalValueAssets() {
    this.totalValuesAssets = {
      total: 0,
      stock: 0,
      options: 0,
      funds: 0,
      bonds: 0,
      commodities: 0,
      cash: 0
    };
  }
}
